"use client"

import type React from "react"
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"

import debounce from "lodash/debounce"

import { FooterText } from "@/components/flow/FooterText"
import { FormContainer } from "@/components/flow/FormContainer"
import { HeaderSection } from "@/components/flow/HeaderSection"
import { NavBar } from "@/components/flow/NavBar"
import { ProgressBar } from "@/components/flow/ProgressBar"
import { SubmitButton } from "@/components/flow/SubmitButton"
import { useFramedCheckout } from "@/lib/providers/CheckoutFrameProvider"
import { useIsWebView } from "@/lib/providers/WebViewProvider"

const SPANISH_TEXT = {
  continueButton: "Continuar",
  backButton: "Regresar",
  verifyButton: "Verificar",
}

const FormErrorContext = createContext<(() => void) | null>(null)

export const useFormErrorContext = () => {
  return useContext(FormErrorContext)
}

interface WebNativeStepWrapperProps {
  title?: React.ReactNode
  subtitle?: React.ReactNode
  progressPercentage: number
  children?: React.ReactNode
  isLoading?: boolean
  formError?: string | null
  onSubmit?: (e: React.FormEvent) => void
  submitDisabled?: boolean
  submitButtonText?: React.ReactNode
  hideSubmitButton?: boolean
  showBackButton?: boolean
  onBack?: () => void
  footerText?: React.ReactNode
  topSection?: React.ReactNode
  bottomSection?: React.ReactNode
  invertedButtonStyle?: boolean
  submitTestId?: string
  // Framed chassis only: actions pinned to the bottom of the flow (e.g. step CTAs that
  // would otherwise live in children). Falls back to the built-in submit button.
  footerActions?: React.ReactNode
}

export const StepWrapper: React.FC<WebNativeStepWrapperProps> = ({
  title,
  subtitle,
  progressPercentage,
  children,
  isLoading = false,
  formError,
  onSubmit,
  submitDisabled = false,
  submitButtonText,
  hideSubmitButton = false,
  showBackButton = false,
  onBack,
  footerText,
  topSection,
  bottomSection,
  invertedButtonStyle = false,
  submitTestId,
  footerActions,
}) => {
  const buttonText = submitButtonText === undefined ? SPANISH_TEXT.continueButton : submitButtonText
  const [internalFormError, setInternalFormError] = useState<string | null>(formError || null)
  const isWebView = useIsWebView()
  const framed = useFramedCheckout()
  // Keyboard/viewport machinery runs for the app webview and every non-framed flow (incl. the
  // marketplace web signup form, where isWebView is always false). Skip it only for the framed
  // hosted checkout, which lays out via pure CSS. Gating on isWebView alone skipped it for 100%
  // of web signups, so the Android keyboard occluded the form.
  const handlesKeyboardViewport = isWebView || !framed

  useEffect(() => {
    setInternalFormError(formError || null)
  }, [formError])

  // Create stable, debounced event handlers with proper cleanup
  const adjustHeight = useCallback(() => {
    requestAnimationFrame(() => {
      if (!handlesKeyboardViewport || typeof window === "undefined" || !scrollContainerRef.current) return

      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty("--vh", `${vh}px`)
      scrollContainerRef.current.style.height = `calc(100 * var(--vh, 1vh))`

      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
      const isAndroid = /Android/i.test(navigator.userAgent)

      if (isIOS) {
        document.body.style.paddingBottom = "max(var(--nelo-safe-bottom, env(safe-area-inset-bottom, 0px)), 20px)"
      } else if (isAndroid) {
        const deviceHeight = Math.max(window.innerHeight, screen.height)
        document.documentElement.style.setProperty("--android-height", `${deviceHeight}px`)
        scrollContainerRef.current.style.maxHeight = `var(--android-height, 100vh)`
      }
    })
  }, [handlesKeyboardViewport])

  const debouncedAdjustHeight = useMemo(() => debounce(() => adjustHeight(), 100), [adjustHeight])

  const handleKeyboardHide = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.overflow = "auto"
    }
  }, [])

  const checkForKeyboard = useCallback(() => {
    requestAnimationFrame(() => {
      if (typeof window === "undefined") return
      adjustHeight()
      handleKeyboardHide()
    })
  }, [adjustHeight, handleKeyboardHide])

  const debouncedCheckForKeyboard = useMemo(() => debounce(() => checkForKeyboard(), 100), [checkForKeyboard])

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      if (typeof document !== "undefined" && document.activeElement === document.body) {
        handleKeyboardHide()
      }
    }, 100)
  }, [handleKeyboardHide])

  useEffect(() => {
    if (!handlesKeyboardViewport) return

    // Initial height adjustment
    adjustHeight()

    const isAndroid = typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent)

    if (typeof window !== "undefined") {
      window.addEventListener("resize", debouncedAdjustHeight)
      window.addEventListener("orientationchange", debouncedAdjustHeight)

      if (isAndroid) {
        window.addEventListener("resize", debouncedCheckForKeyboard)
      }
    }

    if (typeof document !== "undefined") {
      document.addEventListener("blur", handleBlur, true)
    }

    // Cleanup function with proper function references
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", debouncedAdjustHeight)
        window.removeEventListener("orientationchange", debouncedAdjustHeight)
        if (isAndroid) {
          window.removeEventListener("resize", debouncedCheckForKeyboard)
        }
      }
      if (typeof document !== "undefined") {
        document.removeEventListener("blur", handleBlur, true)
      }

      // Cancel any pending debounced calls
      debouncedAdjustHeight.cancel()
      if (isAndroid) {
        debouncedCheckForKeyboard.cancel()
      }
    }
  }, [handlesKeyboardViewport, adjustHeight, debouncedAdjustHeight, debouncedCheckForKeyboard, handleBlur])

  const clearFormError = () => {
    if (internalFormError) {
      setInternalFormError(null)
    }
  }
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.overflow = "auto"
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      })
    }
  }

  const headerRow = (
    <>
      <NavBar showBackButton={showBackButton} onBack={onBack} />
      <ProgressBar percentage={progressPercentage} />
    </>
  )

  // Render nothing when there's no button to show — otherwise hideSubmitButton leaves an empty,
  // focusable role="button" pinned in the footer (a phantom "button" stop for screen readers).
  const submitBlock = submitButtonText !== "" && !hideSubmitButton && (
    <div
      className="flex-none"
      role="button"
      tabIndex={0}
      data-testid={submitTestId}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          const isAndroid = typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent)
          if (isAndroid && typeof document !== "undefined") {
            const activeElement = document.activeElement as HTMLElement
            if (activeElement && activeElement.blur) {
              activeElement.blur()
            }
          }
        }
      }}
      onClick={() => {
        const isAndroid = typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent)
        if (isAndroid && typeof document !== "undefined") {
          const activeElement = document.activeElement as HTMLElement
          if (activeElement && activeElement.blur) {
            activeElement.blur()
          }
        }
      }}
    >
      <SubmitButton
        text={buttonText}
        isLoading={isLoading}
        disabled={submitDisabled}
        variant={invertedButtonStyle ? "inverted" : "primary"}
        onClick={() => {
          if (internalFormError) {
            scrollToTop()
          }
        }}
      />
    </div>
  )

  const topAndHeader = (
    <>
      {topSection && (
        <div className="mb-4">
          {topSection}
          <div className="mt-6 h-px w-full bg-gray-200" />
        </div>
      )}
      <HeaderSection title={typeof title === "string" ? title.trim() : title} subtitle={typeof subtitle === "string" ? subtitle.trim() : subtitle} />
      <FormContainer onSubmit={onSubmit} formError={internalFormError}>
        {children}
      </FormContainer>
    </>
  )

  const footerSections = (
    <>
      {footerText && (
        <div className="mb-16">
          <FooterText text={footerText} />
        </div>
      )}
      {bottomSection && (
        <div className="mb-4">
          <div className="mb-6 h-px w-full bg-gray-200" />
          {bottomSection}
        </div>
      )}
    </>
  )

  // Framed chassis (standalone BNPL checkout): full white flow, header pinned top, body
  // scrollable with a hidden scrollbar, actions pinned to the bottom.
  if (framed) {
    const pinnedFooter = footerActions ?? submitBlock
    return (
      <FormErrorContext.Provider value={clearFormError}>
        <div
          className="bg-nelo-blue font-nunito relative flex h-[100dvh] w-full justify-center overflow-hidden p-4"
          style={{
            paddingTop: "calc(var(--nelo-safe-top, env(safe-area-inset-top, 0px)) + 1rem)",
            paddingBottom: "calc(var(--nelo-safe-bottom, env(safe-area-inset-bottom, 0px)) + 1rem)",
          }}
        >
          <div className="bg-surface flex w-full max-w-md flex-col overflow-hidden rounded-2xl px-5">
            <div className="flex-none pt-3">{headerRow}</div>
            <div ref={scrollContainerRef} className="flex-1 [scrollbar-width:none] overflow-y-auto pt-4 [&::-webkit-scrollbar]:hidden">
              <div className="flex flex-col">
                {topAndHeader}
                {footerSections}
              </div>
            </div>
            {pinnedFooter && <div className="flex-none pt-3 pb-6">{pinnedFooter}</div>}
          </div>
        </div>
      </FormErrorContext.Provider>
    )
  }

  return (
    <FormErrorContext.Provider value={clearFormError}>
      <div
        ref={scrollContainerRef}
        className="font-nunito bg-surface relative flex h-full w-full flex-col overflow-x-hidden overflow-y-auto"
        style={{
          paddingTop: "var(--nelo-safe-top, env(safe-area-inset-top, 0px))",
          paddingBottom: "max(var(--nelo-safe-bottom, env(safe-area-inset-bottom, 0px)), 20px)",
          minHeight: "100%",
        }}
      >
        <div className="app-chrome-sticky bg-surface sticky top-0 z-40 shadow-sm">{headerRow}</div>

        <div className="mx-auto w-11/12 flex-1 px-0 pt-6 pb-10">
          <div className="flex flex-col">
            {topAndHeader}
            {submitBlock && <div className="mb-6">{submitBlock}</div>}
            {footerSections}
          </div>
        </div>
      </div>
    </FormErrorContext.Provider>
  )
}
